import { Button, Grid, MenuItem, Select } from '@mui/material'
import React from 'react'
import { DateRange } from '../../../generated/graphql'
import dayjs, { Dayjs } from 'dayjs'
import quarterOfYear from 'dayjs/plugin/quarterOfYear'
import DateField from '../dateField'

dayjs.extend(quarterOfYear)

enum RelativeDateRange {
    Today = 'Today',
    ThisWeek = 'This week',
    Quarter = 'Quarter',
    ThisMonth = 'This month',
    ThisYear = 'This year',
    Custom = 'Custom',
}

type ComponentProps = {
    onChange: (dateRange: DateRange) => void
}

const RelativeDatePicker = ({ onChange }: ComponentProps) => {
    const [customSelector, setCustomSelector] = React.useState<DateRange>()
    const [selected, setSelected] = React.useState<string>(
        RelativeDateRange.Quarter
    )

    const setEndHour = (day: Dayjs) => {
        return day.set('hour', 23).set('minute', 59).set('second', 59)
    }

    const setStartHour = (day: Dayjs) => {
        return day.set('hour', 0).set('minute', 0).set('second', 0)
    }

    const getRangeDatesFromQuarter = React.useCallback(() => {
        const quarter = dayjs().quarter()
        const start = dayjs().quarter(quarter).startOf('quarter')
        const end = dayjs().quarter(quarter).endOf('quarter')
        return {
            from: setStartHour(start).toDate(),
            to: setEndHour(end).toDate(),
        }
    }, [])

    const getDateRangeFromRelative = React.useCallback(
        (relative: RelativeDateRange): DateRange => {
            switch (relative) {
                case RelativeDateRange.Today:
                    return {
                        from: setStartHour(dayjs()).toDate(),
                        to: setEndHour(dayjs()).toDate(),
                    }
                case RelativeDateRange.ThisWeek:
                    return {
                        from: setStartHour(
                            dayjs().subtract(1, 'week')
                        ).toDate(),
                        to: setEndHour(dayjs()).toDate(),
                    }
                case RelativeDateRange.Quarter:
                    return getRangeDatesFromQuarter()
                case RelativeDateRange.ThisMonth:
                    return {
                        from: setStartHour(dayjs().startOf('month')).toDate(),
                        to: setEndHour(dayjs().endOf('month')).toDate(),
                    }
                case RelativeDateRange.ThisYear:
                    return {
                        from: setStartHour(dayjs().startOf('year')).toDate(),
                        to: setEndHour(dayjs().endOf('year')).toDate(),
                    }
                default:
                    return {
                        from: setStartHour(dayjs()).toDate(),
                        to: setEndHour(dayjs()).toDate(),
                    }
            }
        },
        [getRangeDatesFromQuarter]
    )

    React.useEffect(() => {
        onChange(getDateRangeFromRelative(RelativeDateRange.Quarter))
    }, [getDateRangeFromRelative, onChange])

    const handleDateRangeChange = React.useCallback(
        (e: { target: { value: string } }) => {
            setSelected(e.target.value)
            if (e.target.value === RelativeDateRange.Custom) {
                return
            }
            onChange(
                getDateRangeFromRelative(e.target.value as RelativeDateRange)
            )
        },
        [getDateRangeFromRelative, onChange]
    )

    return (
        <Grid display={'flex'} flexDirection={'row'} gap={2}>
            <Select
                size="small"
                onChange={handleDateRangeChange}
                value={selected}
            >
                <MenuItem value={RelativeDateRange.Today}>Today</MenuItem>
                <MenuItem value={RelativeDateRange.ThisWeek}>
                    This week
                </MenuItem>
                <MenuItem value={RelativeDateRange.Quarter}>Quarter</MenuItem>
                <MenuItem value={RelativeDateRange.ThisMonth}>
                    This month
                </MenuItem>
                <MenuItem value={RelativeDateRange.ThisYear}>
                    This year
                </MenuItem>
                <MenuItem value={RelativeDateRange.Custom}>Custom</MenuItem>
            </Select>
            {selected === RelativeDateRange.Custom && (
                <>
                    <DateField
                        value={customSelector?.from}
                        label="From date"
                        onChange={(e) =>
                            setCustomSelector((prev) => ({ ...prev, from: e }))
                        }
                    />
                    <DateField
                        value={customSelector?.to}
                        label="To date"
                        onChange={(e) =>
                            setCustomSelector((prev) => ({ ...prev, to: e }))
                        }
                    />
                    <Button
                        variant={'contained'}
                        onClick={() => {
                            if (!customSelector?.from || !customSelector?.to)
                                return
                            onChange(customSelector)
                        }}
                    >
                        Apply
                    </Button>
                </>
            )}
        </Grid>
    )
}

export default React.memo(RelativeDatePicker)
